import {
  Calendar,
  CalendarCreate,
  CalendarUpdate,
  CalendarStats,
  PaginatedResponse,
  BulkSyncResponse,
} from '../types';
import { ApiClient } from '../../../utils/apiClient';
import { handleApiResult } from '../../../utils/apiErrorHandler';

export const calendarIntegrationService = {
  // List all calendars
  list: async (): Promise<Calendar[]> => {
    const response = await ApiClient.get('/calendars/calendars/');
    // Backend may return either an array or a paginated object; handle both
    const result = await handleApiResult<Calendar[] | PaginatedResponse<Calendar>>(
      response,
      'Failed to fetch calendars'
    );
    if (!result.success || !result.data) {
      throw new Error(result.errors?.generalError || 'Failed to fetch calendars');
    }
    return Array.isArray(result.data) ? result.data : result.data.results;
  },

  // Get single calendar
  get: async (id: string): Promise<Calendar> => {
    const response = await ApiClient.get(`/calendars/calendars/${id}/`);
    const result = await handleApiResult<Calendar>(response, 'Failed to fetch calendar');
    if (!result.success || !result.data) {
      throw new Error(result.errors?.generalError || 'Failed to fetch calendar');
    }
    return result.data;
  },

  // Create calendar
  create: async (data: CalendarCreate): Promise<Calendar> => {
    const response = await ApiClient.post('/calendars/calendars/', data);
    const result = await handleApiResult<Calendar>(response, 'Failed to create calendar');
    if (!result.success || !result.data) {
      throw new Error(result.errors?.generalError || 'Failed to create calendar');
    }
    return result.data;
  },

  // Update calendar
  update: async (id: string, data: CalendarUpdate): Promise<Calendar> => {
    const response = await ApiClient.patch(`/calendars/calendars/${id}/`, data);
    const result = await handleApiResult<Calendar>(response, 'Failed to update calendar');
    if (!result.success || !result.data) {
      throw new Error(result.errors?.generalError || 'Failed to update calendar');
    }
    return result.data;
  },

  // Delete calendar
  delete: async (id: string): Promise<void> => {
    const response = await ApiClient.delete(`/calendars/calendars/${id}/`);
    const result = await handleApiResult<void>(response, 'Failed to delete calendar');
    if (!result.success) {
      throw new Error(result.errors?.generalError || 'Failed to delete calendar');
    }
  },

  // Refresh token
  refreshToken: async (id: string): Promise<{ message: string }> => {
    const response = await ApiClient.post(`/calendars/calendars/${id}/refresh_token/`);
    const result = await handleApiResult<{ message: string }>(response, 'Failed to refresh token');
    if (!result.success || !result.data) {
      throw new Error(result.errors?.generalError || 'Failed to refresh token');
    }
    return result.data;
  },

  // Sync events for calendar
  syncEvents: async (id: string): Promise<{ message: string }> => {
    const response = await ApiClient.post(`/calendars/calendars/${id}/sync_events/`);
    const result = await handleApiResult<{ message: string }>(response, 'Failed to sync events');
    if (!result.success || !result.data) {
      throw new Error(result.errors?.generalError || 'Failed to sync events');
    }
    return result.data;
  },

  // Bulk sync
  bulkSync: async (calendarIds: string[]): Promise<BulkSyncResponse> => {
    const response = await ApiClient.post('/calendars/calendars/bulk_sync/', {
      calendar_ids: calendarIds,
    });
    const result = await handleApiResult<BulkSyncResponse>(
      response,
      'Failed to bulk sync calendars'
    );
    if (!result.success || !result.data) {
      throw new Error(result.errors?.generalError || 'Failed to bulk sync calendars');
    }
    return result.data;
  },

  // Get calendar stats
  getStats: async (): Promise<CalendarStats> => {
    const response = await ApiClient.get('/calendars/calendars/stats/');
    const result = await handleApiResult<CalendarStats>(response, 'Failed to fetch calendar stats');
    if (!result.success || !result.data) {
      throw new Error(result.errors?.generalError || 'Failed to fetch calendar stats');
    }
    return result.data;
  },
};
